import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  InputAdornment,
  Paper,
  Autocomplete,
  Divider
} from "@mui/material";
import CallSplitRoundedIcon from "@mui/icons-material/CallSplitRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import UserDataService from "../pages/services/UserService";
import StampDutyService from "../pages/services/StampDutyService";
import { fNumber } from "../utils/formatNumber";

// Parse any comma-separated or raw string into clean numeric float
const parseNum = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  const clean = String(val).replace(/,/g, "").trim();
  const n = parseFloat(clean);
  return Number.isFinite(n) ? n : 0;
};

// Formats input with thousands commas as user types, preserving up to 2 decimals
const formatInputWithCommas = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const clean = String(val).replace(/[^0-9.]/g, "");
  const parts = clean.split(".");
  const intPart = parts[0] ? Number(parts[0]).toLocaleString("en-US") : "";
  if (parts.length > 1) {
    return `${intPart}.${parts[1].slice(0, 2)}`;
  }
  return intPart;
};

// Formats number to standard currency string e.g. "4,000,000.00" on blur or auto-fill
const formatToCurrencyString = (val) => {
  const n = parseNum(val);
  if (n === 0 && (!val || String(val).trim() === "")) return "";
  return fNumber(n, 2);
};

export default function SplitDepositModal({ open, onClose, onSuccess, initialMemberId }) {
  const [members, setMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState(initialMemberId || "");
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Form fields (strings with thousand commas)
  const [totalDeposit, setTotalDeposit] = useState("");
  const [savingsAmt, setSavingsAmt] = useState("");
  const [sharesAmt, setSharesAmt] = useState("");
  const [loanAmt, setLoanAmt] = useState("");
  const [ileyaAmt, setIleyaAmt] = useState("");
  const [takafulAmt, setTakafulAmt] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  // Stamp duty fields
  const [applyStampDuty, setApplyStampDuty] = useState(true);
  const [stampDutyAmount, setStampDutyAmount] = useState(50);
  const [deductDutyFromDeposit, setDeductDutyFromDeposit] = useState(true);
  const [waiveReason, setWaiveReason] = useState("");
  const [notes, setNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Load members if not provided
  useEffect(() => {
    if (!open) return;
    if (initialMemberId) {
      setSelectedMemberId(initialMemberId);
    }
    setLoadingMembers(true);
    UserDataService.getAll()
      .then(res => {
        const list = res?.data || res || [];
        setMembers(Array.isArray(list) ? list : []);
      })
      .catch(() => {})
      .finally(() => setLoadingMembers(false));
  }, [open, initialMemberId]);

  const memberOptions = useMemo(() => {
    return members.map(m => {
      const id = String(m._id || m.id);
      const fname = (m.firstName || m.firstname || m.first_name || "").trim();
      const lname = (m.lastName || m.lastname || u_last(m) || "").trim();
      const fullName = (lname ? `${lname} ${fname}`.trim() : fname) || (m.username || m.name || "").trim() || "Member";
      const regNo = (m.registrationNo || m.membershipId || "").trim();
      const label = regNo ? `${fullName} (${regNo})` : fullName;
      return {
        id,
        label,
        fullName,
        regNo,
        raw: m
      };
    }).sort((a, b) => a.fullName.localeCompare(b.fullName));

    function u_last(item) {
      return item.lastName || item.lastname || item.last_name || "";
    }
  }, [members]);

  const selectedMemberOption = useMemo(() => {
    return memberOptions.find(opt => opt.id === String(selectedMemberId)) || null;
  }, [memberOptions, selectedMemberId]);

  const selectedMember = useMemo(() => {
    return members.find(m => String(m._id || m.id) === String(selectedMemberId)) || null;
  }, [members, selectedMemberId]);

  // Numeric values for real-time calculations
  const totalDepNum = parseNum(totalDeposit);
  const sNum = parseNum(savingsAmt);
  const shNum = parseNum(sharesAmt);
  const lNum = parseNum(loanAmt);
  const iNum = parseNum(ileyaAmt);
  const tNum = parseNum(takafulAmt);
  const sdNum = applyStampDuty ? (parseNum(stampDutyAmount) || 50) : 0;

  // Deductions: Loan Repayment + Stamp Duty (if deducted from this deposit)
  const dutyDeduction = deductDutyFromDeposit ? sdNum : 0;
  const fixedAllocations = lNum + iNum + tNum + dutyDeduction;
  const totalDeductions = fixedAllocations;

  // IMMEDIATE DEFICIT ALERT: Deposit is less than fixed allocations + duty
  const isDeficit = (totalDepNum > 0 || fixedAllocations > 0) && totalDepNum < fixedAllocations;
  const deficitAmount = isDeficit ? Number((fixedAllocations - totalDepNum).toFixed(2)) : 0;

  // Net balance available for 30% Shares and 70% Savings
  const netBalance = Math.max(0, Number((totalDepNum - fixedAllocations).toFixed(2)));

  // 30% Shares and 70% Savings Formula Calculations
  const formulaShares = Number((netBalance * 0.30).toFixed(2));
  const formulaSavings = Number((netBalance - formulaShares).toFixed(2)); // Sum always matches netBalance exactly

  // Sum check across all accounts
  const totalAllocated = sNum + shNum + lNum + iNum + tNum;
  const totalAccounted = totalAllocated + dutyDeduction;
  const remaining = Number((totalDepNum - totalAccounted).toFixed(2));
  // Balanced if exact match, or if stamp duty is waived and remainder is within the waived duty amount
  const isBalanced = totalDepNum > 0 && (Math.abs(remaining) <= 0.01 || (!applyStampDuty && Math.abs(remaining) <= 50.01)) && !isDeficit;

  // Handler: Apply 30% Shares & 70% Savings Formula
  const handleApplyFormula = () => {
    if (isDeficit || totalDepNum <= 0) return;
    setSharesAmt(fNumber(formulaShares, 2));
    setSavingsAmt(fNumber(formulaSavings, 2));
  };

  // Handler: Toggling Stamp Duty Waiver dynamically recalculates/rebalances the split
  const handleToggleStampDuty = (checked) => {
    setApplyStampDuty(checked);

    if (totalDepNum > 0 && (sNum > 0 || shNum > 0)) {
      const newDuty = (checked && deductDutyFromDeposit) ? (parseNum(stampDutyAmount) || 50) : 0;
      const newNetBalance = Math.max(0, Number((totalDepNum - lNum - iNum - tNum - newDuty).toFixed(2)));
      const newShares = Number((newNetBalance * 0.30).toFixed(2));
      const newSavings = Number((newNetBalance - newShares).toFixed(2));
      setSharesAmt(fNumber(newShares, 2));
      setSavingsAmt(fNumber(newSavings, 2));
    }
  };

  // Handler: Changing Preset or Custom Stamp Duty Amount dynamically recalculates if duty is deducted
  const handleStampDutyAmountChange = (val) => {
    setStampDutyAmount(val);
    if (applyStampDuty && deductDutyFromDeposit && totalDepNum > 0 && (sNum > 0 || shNum > 0)) {
      const newDuty = parseNum(val) || 0;
      const newNetBalance = Math.max(0, Number((totalDepNum - lNum - iNum - tNum - newDuty).toFixed(2)));
      const newShares = Number((newNetBalance * 0.30).toFixed(2));
      const newSavings = Number((newNetBalance - newShares).toFixed(2));
      setSharesAmt(fNumber(newShares, 2));
      setSavingsAmt(fNumber(newSavings, 2));
    }
  };

  // Handler: Toggle deducting duty from deposit vs external payment
  const handleToggleDeductDuty = (checked) => {
    setDeductDutyFromDeposit(checked);
    if (totalDepNum > 0 && (sNum > 0 || shNum > 0)) {
      const newDuty = (applyStampDuty && checked) ? (parseNum(stampDutyAmount) || 50) : 0;
      const newNetBalance = Math.max(0, Number((totalDepNum - lNum - iNum - tNum - newDuty).toFixed(2)));
      const newShares = Number((newNetBalance * 0.30).toFixed(2));
      const newSavings = Number((newNetBalance - newShares).toFixed(2));
      setSharesAmt(fNumber(newShares, 2));
      setSavingsAmt(fNumber(newSavings, 2));
    }
  };

  // Quick Action: Absorb any remaining kobo/naira into Savings
  const handleAddRemainingToSavings = () => {
    if (remaining === 0) return;
    const newSavings = Math.max(0, Number((sNum + remaining).toFixed(2)));
    setSavingsAmt(fNumber(newSavings, 2));
  };

  const handleSubmit = async () => {
    setError("");
    setSuccessMsg("");

    if (!selectedMemberId) {
      setError("Please select a member.");
      return;
    }
    if (totalDepNum <= 0) {
      setError("Please enter a valid total deposit amount.");
      return;
    }
    if (isDeficit) {
      setError(`Deposit amount (₦${fNumber(totalDepNum, 2)}) is less than total deductions to remove (₦${fNumber(totalDeductions, 2)}). Shortfall deficit: ₦${fNumber(deficitAmount, 2)}.`);
      return;
    }

    let finalSavings = sNum;
    let finalAccounted = totalAccounted;
    let finalRemaining = remaining;

    // Auto-absorb any remaining ₦50 when stamp duty is waived so the officer is never blocked
    if (!applyStampDuty && Math.abs(remaining) > 0.01 && Math.abs(remaining) <= 50.01) {
      finalSavings = Math.max(0, Number((sNum + remaining).toFixed(2)));
      finalAccounted = totalDepNum;
      finalRemaining = 0;
      setSavingsAmt(fNumber(finalSavings, 2));
    }

    if (Math.abs(finalRemaining) > 0.01) {
      setError(`Total accounted (₦${fNumber(finalAccounted, 2)}) must match the total deposit (₦${fNumber(totalDepNum, 2)}). Remaining unallocated: ₦${fNumber(finalRemaining, 2)}`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        memberId: selectedMemberId,
        depositAmount: totalDepNum,
        stampDutyAmount: applyStampDuty ? sdNum : 0,
        accountType: "split",
        splitDistribution: {
          savings: finalSavings,
          shares: shNum,
          loan: lNum,
          ileya: iNum,
          takaful: tNum
        },
        isWaived: !applyStampDuty,
        waiveReason: !applyStampDuty ? (waiveReason || "Waived: Single deposit distributed across multiple accounts") : "",
        date,
        notes: notes.trim() || `Single bulk deposit distributed to Savings (₦${fNumber(finalSavings, 2)}), Shares (₦${fNumber(shNum, 2)}), Loan Repayment (₦${fNumber(lNum, 2)})${iNum > 0 ? `, Ileya (₦${fNumber(iNum, 2)})` : ""}${tNum > 0 ? `, Takaful (₦${fNumber(tNum, 2)})` : ""}${dutyDeduction > 0 ? `, Stamp Duty (₦${fNumber(dutyDeduction, 2)})` : ""}`,
        autoCreditAccounts: true
      };

      const res = await StampDutyService.record(payload);
      setSuccessMsg(res?.data?.message || "Deposit and Stamp Duty successfully processed!");
      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 1200);
    } catch (err) {
      console.error("Split deposit error:", err);
      setError(err?.response?.data?.error || err.message || "Failed to process split deposit.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 2, sm: 3 },
          m: { xs: 1, sm: 2 },
          maxHeight: { xs: "96vh", sm: "92vh" }
        }
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #0B3B60 0%, #0D9488 100%)",
          color: "#ffffff",
          p: { xs: 2, sm: 2.5 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CallSplitRoundedIcon sx={{ fontSize: 28, color: "#34D399" }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: "1.05rem", sm: "1.25rem" } }}>
              Single Deposit Split &amp; Unified Stamp Duty
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)", display: "block" }}>
              Distribute 1 bank transfer into Savings, Shares, and Loan with automatic 70/30 formula splitting
            </Typography>
          </Box>
        </Box>
        <Button onClick={onClose} sx={{ color: "#ffffff", minWidth: "auto" }}>
          <CloseRoundedIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, mt: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

        {/* IMMEDIATE DEFICIT SIGNAL ALERT */}
        {isDeficit && (
          <Alert
            severity="error"
            icon={<WarningAmberRoundedIcon sx={{ fontSize: 30, color: "#DC2626" }} />}
            sx={{
              mb: 2.5,
              border: "2px solid #EF4444",
              backgroundColor: "#FEF2F2",
              borderRadius: 2.5,
              boxShadow: "0 6px 18px rgba(239, 68, 68, 0.18)",
              "& .MuiAlert-message": { width: "100%" }
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, color: "#991B1B", fontSize: "0.95rem" }}>
                🚨 INSUFFICIENT DEPOSIT ALERT
              </Typography>
              <Chip
                label={`Shortfall Deficit: −₦${fNumber(deficitAmount, 2)}`}
                color="error"
                size="small"
                sx={{ fontWeight: 900, fontSize: "0.8rem", letterSpacing: 0.5 }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: "#7F1D1D", mt: 0.75, lineHeight: 1.5 }}>
              The total deposit amount (<strong>₦{fNumber(totalDepNum, 2)}</strong>) is <strong>LESS</strong> than the total deductions to be removed (<strong>₦{fNumber(totalDeductions, 2)}</strong> = Loan Repayment ₦{fNumber(lNum, 2)} + Stamp Duty ₦{fNumber(dutyDeduction, 2)}).
            </Typography>
            <Typography variant="caption" sx={{ color: "#B91C1C", fontWeight: 800, display: "block", mt: 0.75 }}>
              No remaining balance exists to share into 30% Shares and 70% Savings. Please increase the deposit or reduce the loan repayment amount.
            </Typography>
          </Alert>
        )}

        <Grid container spacing={{ xs: 2, sm: 2.5 }}>
          {/* Member Selection with Incremental Text Filtering */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={memberOptions}
              loading={loadingMembers}
              value={selectedMemberOption}
              onChange={(event, newValue) => {
                setSelectedMemberId(newValue ? newValue.id : "");
              }}
              getOptionLabel={(option) => {
                if (typeof option === "string") return option;
                return option?.label || "";
              }}
              isOptionEqualToValue={(option, value) => {
                return String(option.id) === String(value.id);
              }}
              filterOptions={(options, state) => {
                const query = (state.inputValue || "").toLowerCase().trim();
                if (!query) return options;
                return options.filter(opt => {
                  const label = (opt.label || "").toLowerCase();
                  const reg = (opt.regNo || "").toLowerCase();
                  const full = (opt.fullName || "").toLowerCase();
                  return label.includes(query) || reg.includes(query) || full.includes(query);
                });
              }}
              disabled={loadingMembers || submitting}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  key={option.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    px: 1.5,
                    borderBottom: "1px solid #F1F5F9"
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#1E293B" }}>
                      {option.fullName}
                    </Typography>
                    {option.regNo ? (
                      <Typography variant="caption" sx={{ color: "#0D9488", fontWeight: 600 }}>
                        ID / Reg: {option.regNo}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                        Registered Member
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Member *"
                  placeholder="Type name or ID to filter..."
                  helperText="Search by first name, last name, or registration number"
                  required={!selectedMemberId}
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingMembers ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Date */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Deposit Date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={submitting}
            />
          </Grid>

          {/* Member Current Balances Card */}
          {selectedMember && (
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: "#F8FAFC",
                  borderColor: "#CBD5E1",
                  borderRadius: 2,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "center"
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
                  Current Balances:
                </Typography>
                <Chip
                  label={`Savings: ₦${fNumber(selectedMember.savingsBalance || 0, 2)}`}
                  size="small"
                  sx={{ fontWeight: 700, backgroundColor: "#ECFDF5", color: "#059669" }}
                />
                <Chip
                  label={`Shares: ₦${fNumber(selectedMember.sharesBalance || 0, 2)}`}
                  size="small"
                  sx={{ fontWeight: 700, backgroundColor: "#EFF6FF", color: "#2563EB" }}
                />
                <Chip
                  label={`Loan Debt: ₦${fNumber(selectedMember.loanBalance || 0, 2)}`}
                  size="small"
                  sx={{ fontWeight: 700, backgroundColor: "#FEF2F2", color: "#DC2626" }}
                />
                {selectedMember.loanBalance > 0 && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setLoanAmt(formatToCurrencyString(selectedMember.loanBalance))}
                    sx={{ fontSize: "0.75rem", fontWeight: 800, color: "#DC2626", textTransform: "none", py: 0 }}
                  >
                    ↳ Use Full Loan Debt for Repayment
                  </Button>
                )}
              </Paper>
            </Grid>
          )}

          {/* Total Deposit Amount with comma separation e.g. 4,000,000.00 */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Total Bank Deposit Received (₦) *"
              placeholder="e.g. 4,000,000.00"
              value={totalDeposit}
              onChange={(e) => setTotalDeposit(formatInputWithCommas(e.target.value))}
              onBlur={() => { if (totalDeposit) setTotalDeposit(formatToCurrencyString(totalDeposit)); }}
              InputProps={{
                startAdornment: <InputAdornment position="start" sx={{ fontWeight: 800, color: "#0D9488" }}>₦</InputAdornment>,
                inputMode: "decimal"
              }}
              helperText="Enter deposit amount (formatted with commas e.g. 4,000,000.00)"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: { xs: "1.1rem", sm: "1.25rem" },
                  fontWeight: 800,
                  color: "#0F172A"
                }
              }}
              required
            />
          </Grid>

          {/* STAMP DUTY CONTROL PANEL */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid #CBD5E1",
                backgroundColor: "#F8FAFC"
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <VerifiedUserRoundedIcon sx={{ color: applyStampDuty ? "#0D9488" : "#94A3B8" }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1E293B" }}>
                    Stamp Duty (EMTL) Deduction
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={applyStampDuty}
                      onChange={(e) => handleToggleStampDuty(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={applyStampDuty ? `Apply ₦${fNumber(stampDutyAmount || 50, 2)} Stamp Duty Once` : "Waive Stamp Duty"}
                  sx={{ "& .MuiTypography-root": { fontWeight: 700, fontSize: "0.85rem" } }}
                />
              </Box>

              {applyStampDuty ? (
                <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
                    Duty Amount:
                  </Typography>
                  {[50, 100, 200].map((preset) => (
                    <Button
                      key={preset}
                      size="small"
                      variant={String(stampDutyAmount) === String(preset) ? "contained" : "outlined"}
                      onClick={() => handleStampDutyAmountChange(preset)}
                      sx={{
                        minWidth: 64,
                        py: 0.3,
                        px: 1,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        borderRadius: "6px",
                        bgcolor: String(stampDutyAmount) === String(preset) ? "#0D9488" : "transparent",
                        color: String(stampDutyAmount) === String(preset) ? "#ffffff" : "#0D9488",
                        borderColor: "#0D9488"
                      }}
                    >
                      ₦{preset} {preset === 50 ? "(Std)" : ""}
                    </Button>
                  ))}
                  <TextField
                    size="small"
                    label="Custom Duty (₦)"
                    value={stampDutyAmount}
                    onChange={(e) => handleStampDutyAmountChange(formatInputWithCommas(e.target.value))}
                    onBlur={() => { if (stampDutyAmount) setStampDutyAmount(formatToCurrencyString(stampDutyAmount)); }}
                    sx={{ width: 140, backgroundColor: "#ffffff" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={deductDutyFromDeposit}
                        onChange={(e) => handleToggleDeductDuty(e.target.checked)}
                        color="secondary"
                      />
                    }
                    label={<Typography variant="caption" sx={{ fontWeight: 700, color: "#334155" }}>Deduct ₦{fNumber(sdNum, 2)} from this deposit before 70/30 split</Typography>}
                  />
                </Box>
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="Reason for Waiving Stamp Duty"
                  value={waiveReason}
                  onChange={(e) => setWaiveReason(e.target.value)}
                  placeholder="e.g. Single transfer already charged on another receipt / Officer waiver"
                  sx={{ mt: 1.5, backgroundColor: "#ffffff" }}
                />
              )}
            </Paper>
          </Grid>

          {/* SMART SHARING FORMULA CALCULATOR (Loan + EMTL -> 30% Shares & 70% Savings) */}
          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2.5,
                backgroundColor: isDeficit ? "#FFF1F2" : "#F0FDFA",
                borderColor: isDeficit ? "#FDA4AF" : "#5EEAD4",
                borderWidth: 1.5,
                boxShadow: isDeficit ? "0 4px 14px rgba(244, 63, 94, 0.12)" : "0 4px 14px rgba(13, 148, 136, 0.08)"
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <CalculateRoundedIcon sx={{ color: isDeficit ? "#E11D48" : "#0D9488", fontSize: 26 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: isDeficit ? "#9F1239" : "#0F766E", fontSize: "0.95rem" }}>
                      Split Calculator (Loan + EMTL Deducted → 30% Shares &amp; 70% Savings)
                    </Typography>
                    <Typography variant="caption" sx={{ color: isDeficit ? "#BE123C" : "#115E59", display: "block" }}>
                      Sharing formula: Deposit minus (Loan + Stamp Duty), then 30% to Shares and 70% to Savings
                    </Typography>
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  size="small"
                  disabled={isDeficit || totalDepNum <= 0}
                  onClick={handleApplyFormula}
                  startIcon={<AutoAwesomeRoundedIcon />}
                  sx={{
                    backgroundColor: "#0D9488",
                    fontWeight: 800,
                    textTransform: "none",
                    px: 2,
                    py: 0.75,
                    boxShadow: "0 2px 8px rgba(13, 148, 136, 0.25)",
                    "&:hover": { backgroundColor: "#0F766E" }
                  }}
                >
                  ⚡ Auto-Split (Apply 70/30)
                </Button>
              </Box>

              <Divider sx={{ mb: 1.5, borderColor: isDeficit ? "#FECDD3" : "#CCFBF1" }} />

              {/* Step-by-Step Live Math Cards */}
              <Grid container spacing={1.5}>
                {/* 1. Deposit Received */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: "#ffffff", border: "1px solid #E2E8F0" }}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, display: "block" }}>
                      1. Deposit Received
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0F172A", mt: 0.25 }}>
                      ₦{fNumber(totalDepNum, 2)}
                    </Typography>
                  </Box>
                </Grid>

                {/* 2. Deductions to Remove */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: "#ffffff", border: "1px solid #E2E8F0" }}>
                    <Typography variant="caption" sx={{ color: "#DC2626", fontWeight: 700, display: "block" }}>
                      2. Total Deductions
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#DC2626", mt: 0.25 }}>
                      −₦{fNumber(totalDeductions, 2)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748B", fontSize: "0.7rem", display: "block" }}>
                      Loan ₦{fNumber(lNum, 2)} + EMTL ₦{fNumber(dutyDeduction, 2)}
                    </Typography>
                  </Box>
                </Grid>

                {/* 3. Net Balance to Share */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: "#ffffff", border: "1px solid #E2E8F0" }}>
                    <Typography variant="caption" sx={{ color: isDeficit ? "#DC2626" : "#0D9488", fontWeight: 700, display: "block" }}>
                      3. Balance to Distribute
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isDeficit ? "#DC2626" : "#0D9488", mt: 0.25 }}>
                      {isDeficit ? `Deficit −₦${fNumber(deficitAmount, 2)}` : `₦${fNumber(netBalance, 2)}`}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748B", fontSize: "0.7rem", display: "block" }}>
                      {isDeficit ? "Insufficient funds" : "Ready for 70/30 split"}
                    </Typography>
                  </Box>
                </Grid>

                {/* 4. Calculated Allocations */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: "#ffffff", border: "1px solid #E2E8F0" }}>
                    <Typography variant="caption" sx={{ color: "#2563EB", fontWeight: 700, display: "block" }}>
                      4. 30% / 70% Formula
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#2563EB", fontSize: "0.82rem", mt: 0.25 }}>
                      Shares (30%): ₦{fNumber(formulaShares, 2)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#059669", fontSize: "0.82rem" }}>
                      Savings (70%): ₦{fNumber(formulaSavings, 2)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* SPLIT ALLOCATIONS INPUTS (WITH COMMAS E.G. 4,000,000.00) */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0B3B60" }}>
              Account Allocations Breakdown:
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748B", display: "block", mb: 1 }}>
              Enter or adjust portions manually, or use the 70/30 calculator button above
            </Typography>
          </Grid>

          {/* 1. Loan Repayment Input */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Loan Repayment (₦)"
              placeholder="e.g. 1,000,000.00"
              value={loanAmt}
              onChange={(e) => setLoanAmt(formatInputWithCommas(e.target.value))}
              onBlur={() => { if (loanAmt) setLoanAmt(formatToCurrencyString(loanAmt)); }}
              InputProps={{
                startAdornment: <InputAdornment position="start" sx={{ fontWeight: 700, color: "#DC2626" }}>₦</InputAdornment>,
                inputMode: "decimal"
              }}
              helperText={selectedMember?.loanBalance > 0 ? `Member debt: ₦${fNumber(selectedMember.loanBalance, 2)}` : "Loan repayment portion"}
            />
          </Grid>

          {/* 2. Shares Contribution Input (30% Formula) */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Shares Contribution (30%) (₦)"
              placeholder="e.g. 900,000.00"
              value={sharesAmt}
              onChange={(e) => setSharesAmt(formatInputWithCommas(e.target.value))}
              onBlur={() => { if (sharesAmt) setSharesAmt(formatToCurrencyString(sharesAmt)); }}
              InputProps={{
                startAdornment: <InputAdornment position="start" sx={{ fontWeight: 700, color: "#2563EB" }}>₦</InputAdornment>,
                inputMode: "decimal"
              }}
              helperText={`Suggested 30%: ₦${fNumber(formulaShares, 2)}`}
            />
          </Grid>

          {/* 3. Savings Deposit Input (70% Formula) */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Savings Deposit (70%) (₦)"
              placeholder="e.g. 2,100,000.00"
              value={savingsAmt}
              onChange={(e) => setSavingsAmt(formatInputWithCommas(e.target.value))}
              onBlur={() => { if (savingsAmt) setSavingsAmt(formatToCurrencyString(savingsAmt)); }}
              InputProps={{
                startAdornment: <InputAdornment position="start" sx={{ fontWeight: 700, color: "#059669" }}>₦</InputAdornment>,
                inputMode: "decimal"
              }}
              helperText={`Suggested 70%: ₦${fNumber(formulaSavings, 2)}`}
            />
          </Grid>

          {/* 4. Ileya Savings Deposit Input */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Ileya Savings Deposit (₦)"
              placeholder="e.g. 500,000.00"
              value={ileyaAmt}
              onChange={(e) => setIleyaAmt(formatInputWithCommas(e.target.value))}
              onBlur={() => { if (ileyaAmt) setIleyaAmt(formatToCurrencyString(ileyaAmt)); }}
              InputProps={{
                startAdornment: <InputAdornment position="start" sx={{ fontWeight: 700, color: "#D97706" }}>₦</InputAdornment>,
                inputMode: "decimal"
              }}
              helperText={selectedMember?.ileyaSavingsBalance != null ? `Current balance: ₦${fNumber(selectedMember.ileyaSavingsBalance, 2)}` : "Ileya savings portion"}
            />
          </Grid>

          {/* 5. Takaful Deposit Input */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Takaful Deposit (₦)"
              placeholder="e.g. 200,000.00"
              value={takafulAmt}
              onChange={(e) => setTakafulAmt(formatInputWithCommas(e.target.value))}
              onBlur={() => { if (takafulAmt) setTakafulAmt(formatToCurrencyString(takafulAmt)); }}
              InputProps={{
                startAdornment: <InputAdornment position="start" sx={{ fontWeight: 700, color: "#7C3AED" }}>₦</InputAdornment>,
                inputMode: "decimal"
              }}
              helperText={selectedMember?.takafulBalance != null ? `Current balance: ₦${fNumber(selectedMember.takafulBalance, 2)}` : "Takaful insurance fund portion"}
            />
          </Grid>

          {/* Split Balance Summary Card */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: isBalanced ? "#F0FDF4" : "#FEF2F2",
                border: "1.5px solid",
                borderColor: isBalanced ? "#86EFAC" : "#FECACA",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 800, color: isBalanced ? "#166534" : "#991B1B" }}>
                  Total Accounted: ₦{fNumber(totalAccounted, 2)} of ₦{fNumber(totalDepNum, 2)}
                </Typography>
                <Typography variant="caption" sx={{ color: isBalanced ? "#15803D" : "#B91C1C", display: "block" }}>
                  (Savings: ₦{fNumber(sNum, 2)} + Shares: ₦{fNumber(shNum, 2)} + Loan: ₦{fNumber(lNum, 2)}{iNum > 0 ? ` + Ileya: ₦${fNumber(iNum, 2)}` : ""}{tNum > 0 ? ` + Takaful: ₦${fNumber(tNum, 2)}` : ""}{dutyDeduction > 0 ? ` + EMTL: ₦${fNumber(dutyDeduction, 2)}` : ""})
                </Typography>
              </Box>

              <Chip
                icon={isBalanced ? <CheckCircleRoundedIcon sx={{ color: "#166534 !important" }} /> : <WarningAmberRoundedIcon sx={{ color: "#991B1B !important" }} />}
                label={isBalanced ? "✓ 100% Balanced" : (isDeficit ? `Deficit: −₦${fNumber(deficitAmount, 2)}` : `Remaining: ₦${fNumber(remaining, 2)}`)}
                sx={{
                  fontWeight: 900,
                  fontSize: "0.85rem",
                  backgroundColor: isBalanced ? "#DCFCE7" : "#FEE2E2",
                  color: isBalanced ? "#166534" : "#991B1B",
                  borderColor: isBalanced ? "#86EFAC" : "#FCA5A5",
                  border: "1px solid"
                }}
              />

              {/* Quick auto-balance actions when there is an unallocated remaining amount */}
              {!isBalanced && Math.abs(remaining) > 0.01 && (
                <Box sx={{ width: "100%", mt: 1, pt: 1, borderTop: "1px dashed #FECACA", display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#991B1B" }}>
                    Quick Auto-Balance:
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleApplyFormula}
                    startIcon={<AutoAwesomeRoundedIcon />}
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 800,
                      textTransform: "none",
                      borderColor: "#DC2626",
                      color: "#991B1B",
                      backgroundColor: "#FEF2F2",
                      py: 0.25,
                      "&:hover": { backgroundColor: "#FEE2E2" }
                    }}
                  >
                    ⚡ Auto-Balance into 70/30 Split
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleAddRemainingToSavings}
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 800,
                      textTransform: "none",
                      borderColor: "#059669",
                      color: "#059669",
                      backgroundColor: "#ECFDF5",
                      py: 0.25,
                      "&:hover": { backgroundColor: "#D1FAE5" }
                    }}
                  >
                    + Add ₦{fNumber(remaining, 2)} to Savings
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Transaction Notes / Bank Reference (Optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Jaiz Bank Transfer #TRX-94829"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: { xs: 2, sm: 2.5 }, backgroundColor: "#F8FAFC", borderTop: "1px solid #E2E8F0", flexDirection: { xs: "column-reverse", sm: "row" }, gap: { xs: 1, sm: 1.5 } }}>
        <Button onClick={onClose} disabled={submitting} sx={{ fontWeight: 700, width: { xs: "100%", sm: "auto" } }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || totalDepNum <= 0 || !isBalanced || isDeficit}
          startIcon={submitting ? <CircularProgress size={18} sx={{ color: "#ffffff" }} /> : <CheckCircleRoundedIcon />}
          sx={{
            backgroundColor: "#0D9488",
            fontWeight: 800,
            textTransform: "none",
            px: 3,
            py: 1,
            width: { xs: "100%", sm: "auto" },
            boxShadow: "0 4px 12px rgba(13, 148, 136, 0.25)",
            "&:hover": { backgroundColor: "#0F766E" },
            "&.Mui-disabled": { backgroundColor: "#E2E8F0", color: "#94A3B8" }
          }}
        >
          {submitting ? "Processing..." : (isDeficit ? "Insufficient Deposit" : "Confirm & Post Split Deposit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SplitDepositModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  initialMemberId: PropTypes.string
};
